import { isValidLocale } from "@/lib/i18n/locale";

/**
 * Which surfaces load the PostHog Support (conversations) widget.
 *
 * This is a ROUTE-SCOPING decision made once per page load, not a runtime
 * toggle, because of how posthog-js actually loads the widget
 * (`node_modules/posthog-js/dist/module.js`, class `conversations`):
 *
 *   - `onRemoteConfig()` bails entirely when `config.disable_conversations` is
 *     true, so the "conversations enabled" flag from remote config is never
 *     even recorded.
 *   - `loadIfEnabled()` — the only place that calls
 *     `loadExternalDependency(instance, "conversations", …)` — bails on the
 *     same config value.
 *   - `show()` does NOT force a load: with no manager it logs
 *     "Conversations not loaded yet." and returns.
 *
 * So flipping `set_config({ disable_conversations: false })` after init cannot
 * bring the widget back, and `show()` can only ever REVEAL a widget that
 * remote config already loaded. The decision has to be made at `posthog.init()`
 * against the URL we are initialising on.
 *
 * Why not just enable it everywhere: the widget arrives as an extra external
 * script fetched on the remote-config response — the same shape as
 * `surveys.js`, whose load inside the LCP window regressed hydration on
 * mid-range Android and is the reason `disable_surveys: true` is permanent
 * (see `ensurePostHogInitialized`). The apex feed and job pages are the
 * highest-traffic, most LCP-sensitive, most crawler-dominated surface on the
 * site and get no support value, so they stay out.
 *
 * The employers zone (`/employers`, `/talent`) is a SEPARATE Cloudflare worker
 * (`lokerdollar-employers`), so entering it is always a hard navigation and
 * always re-inits with the widget on. Within the apex SPA a soft navigation to
 * e.g. `/pricing` keeps whatever was decided on the landing page — that gap is
 * covered by `SupportLauncher`, which falls back to the support mailto
 * whenever `posthog.conversations.isAvailable()` is false.
 */
const SUPPORT_WIDGET_SEGMENTS = new Set([
	// B2B: the Shortlist offer, employer dashboard, and the public talent pages
	// the offer links to. Served by the employers zone worker.
	"employers",
	"talent",
	// Apex surfaces where someone is deciding whether to pay, or is already
	// trying to reach us.
	"pricing",
	"contact",
	"feedback",
]);

/** The seeker app zone — every page there is behind login. */
const APP_ZONE_HOSTNAME = "app.lokerdollar.com";

/**
 * True when the PostHog Support widget should be loaded for this page load.
 *
 * Pure and DOM-free so it is unit-testable: callers pass
 * `window.location.hostname` / `window.location.pathname`.
 */
export function shouldEnableSupportWidget({
	hostname,
	pathname,
}: {
	hostname: string;
	pathname: string;
}): boolean {
	if (hostname === APP_ZONE_HOSTNAME) return true;

	const segments = pathname.split("/").filter(Boolean);
	// Locale prefixes are optional in the URL (`/employers` and `/id/employers`
	// are the same page), so drop a leading one before matching. `isValidLocale`
	// is the single source of truth for what counts as one — never re-derive it
	// with a `/[a-z]{2}/` regex, which would also swallow a real first segment.
	const withoutLocale =
		segments.length > 0 && isValidLocale(segments[0])
			? segments.slice(1)
			: segments;

	const first = withoutLocale[0];
	return first !== undefined && SUPPORT_WIDGET_SEGMENTS.has(first);
}
